import { useState } from "react";
import {
  Choices, Details, dimensionError, Disclaimer, NumberField, Nav, Panel, Progress, Question,
  ResultBlock, ResultFooter, ResultHeadline, ResultList, ToolFrame, useTool, useToolEvents, WorkableOptions,
} from "@/components/site/ToolShell";

const SLUG = "golf-simulator-room-fit";

// Ceiling bands are general planning guidance supplied as specification. They are never presented
// as manufacturer guarantees — golfer height, swing, enclosure and launch monitor specs override.
function ceilingBand(h) {
  if (h < 8) return "unsupported";
  if (h < 9) return "limited";
  if (h < 10) return "workable";
  return "comfortable";
}

function evaluate(f) {
  const ceiling = Number(f.ceiling) || 0;
  const width = Number(f.width) || 0;
  const length = Number(f.length) || 0;
  const screenWall = Number(f.screenWall) || 0;
  const band = ceilingBand(ceiling);

  const score = (v, a, b, c) => (v >= c ? 3 : v >= b ? 2 : v >= a ? 1 : 0);
  const dims = [
    { name: "Ceiling height", score: band === "unsupported" ? 0 : band === "limited" ? 1 : band === "workable" ? 2 : 3, value: `${ceiling} ft` },
    { name: "Room width", score: score(width, 10, 12, 14), value: `${width} ft` },
    { name: "Room length", score: score(length, 14, 16, 18), value: `${length} ft` },
    { name: "Screen wall width", score: score(screenWall, 8, 10, 12), value: `${screenWall} ft` },
  ];
  const min = Math.min(...dims.map((d) => d.score));
  const limiting = dims.filter((d) => d.score === min);
  const limitingLabel =
    min >= 3 ? "None — every dimension has margin" : limiting.map((d) => `${d.name} (${d.value})`).join(", ");

  const narrow = dims[1].score < 2;
  const screenWallTooWide = screenWall > width;

  let status, headline, body;
  if (band === "unsupported") {
    status = "Does not support a conventional full-swing setup";
    headline = "Your ceiling is likely too low for a conventional full-swing golf simulator.";
    body =
      ceiling <= 7.4
        ? "At roughly 7 ft, most adults will not have enough overhead clearance to make unrestricted full swings. That does not make the space useless — it makes it a short-game, putting and iron-practice space rather than a full-bag simulator."
        : "Below about 8 ft, most adults will not have enough overhead clearance for unrestricted full swings. Compact practice setups remain possible, but plan this as a practice space rather than a full-bag simulator.";
  } else if (band === "limited") {
    status = "Limited fit — verify your swing and equipment carefully";
    headline = "Limited fit — verify your swing and equipment carefully.";
    body =
      "Between about 8 ft and 9 ft you are in special-case territory. Compact equipment may physically fit, but unrestricted driver and full-club swings may not. Whether this works depends on your height and swing, not on the room alone.";
  } else if (band === "workable") {
    status = "Fit — workable for many setups, with verification";
    headline = "Your ceiling is workable for many golfers and setups.";
    body =
      "Between about 9 ft and 10 ft works for many golfers, but it is conditional rather than guaranteed. Verify your own swing clearance and the specific equipment you intend to buy.";
  } else {
    status = "Fit — comfortable planning benchmark";
    headline = "Your ceiling meets a comfortable planning benchmark.";
    body =
      "10 ft and above is a comfortable planning benchmark for many full-swing installations. It is still not a universal guarantee — taller golfers, steep swing planes and specific enclosures can need more.";
  }

  const reasons = [];
  reasons.push(`Lowest usable ceiling height: ${ceiling} ft.`);
  if (min < 2 && limiting[0].name !== "Ceiling height")
    reasons.push(`${limiting.map((d) => d.name.toLowerCase()).join(" and ")} is the tighter constraint at ${limiting.map((d) => d.value).join(" / ")}.`);
  if (f.handed === "Both")
    reasons.push("Dual-handed use needs usable width on both sides of centre and a launch monitor that reads from both sides.");
  else if (narrow)
    reasons.push(`Width is tight for ${f.handed.toLowerCase()} play — shifting the hitting position away from your trail side is the usual fix.`);

  // Maximum three things to verify.
  const verify = [];
  if (screenWallTooWide)
    verify.push(`Re-measure: the screen wall you entered (${screenWall} ft) is wider than the room width (${width} ft).`);
  if (band === "unsupported" || band === "limited")
    verify.push("Measure your own overhead clearance mid-swing with a club, at the hitting position, not just the room height.");
  verify.push("Check the manufacturer's published installation dimensions and required clearances against your measurements.");
  if (verify.length < 3) verify.push("Confirm launch monitor placement distance and the projector's throw range for your exact mount position.");
  if (verify.length < 3) verify.push("Remember that mat or platform thickness reduces your effective ceiling clearance by the same amount.");

  const screen = [];
  if (screenWallTooWide) screen.push("Your screen cannot exceed the wall it sits on — re-measure before ordering.");
  if (dims[3].score <= 1) screen.push("Screen width is constrained — expect a narrower impact screen and a smaller usable image.");
  else screen.push("Screen wall supports a conventional impact screen width.");
  if (band === "unsupported" || band === "limited")
    screen.push("Low clearance favours a shorter screen and side protection over a full ceiling-height enclosure.");
  else screen.push("A full enclosure with side and ceiling protection is realistic.");
  screen.push("Confirm the screen and enclosure manufacturer's published frame dimensions and clearances.");

  const projector = [];
  if (dims[2].score <= 1) projector.push("Short room length points toward short-throw projection so the light path clears the golfer.");
  else projector.push("Room length supports either a short-throw or a ceiling-mounted longer-throw projector.");
  if (band === "unsupported" || band === "limited")
    projector.push("Low ceilings require careful mount depth — the projector body and mount must sit above the swing path.");
  projector.push("Throw ratio, optical zoom and lens shift must be checked against your exact mount position and target image width.");

  const install = [];
  install.push(
    f.handed === "Both"
      ? "Centre the hitting position and confirm dual-handed capability per device."
      : `Plan the hitting position for ${f.handed.toLowerCase()} play, allowing clearance on your trail side.`
  );
  install.push("Every inch of mat or platform thickness reduces your effective ceiling clearance by the same amount.");
  install.push("Check for anything inside the swing zone — door tracks, openers, light fixtures, joists, ductwork and beams.");

  // Workable configurations, derived from the band and dimensions already calculated above.
  // Nothing new is computed and no product is invented — this only presents what the existing
  // result already supports, so an unsupported ceiling never surfaces a full-swing setup.
  const workable = [];
  const shortThrow = dims[2].score <= 1;
  if (band === "unsupported") {
    workable.push("Short-game and iron practice with a compact hitting area");
    workable.push("Putting practice setup");
    workable.push("Launch-monitor practice without a full-swing enclosure");
    workable.push("Shorter impact screen with side protection rather than a full ceiling-height enclosure");
  } else if (band === "limited") {
    workable.push("Compact full-swing setup — only after you verify your own overhead clearance mid-swing");
    workable.push("Short-game, iron and putting practice, which fit comfortably at this height");
    workable.push("Shorter impact screen with side protection instead of a full ceiling-height enclosure");
    workable.push(shortThrow ? "Short-throw projection so the light path clears the golfer" : "Ceiling-mounted or short-throw projection, with mount depth above the swing path");
  } else {
    workable.push(
      band === "workable"
        ? "Conventional full-swing setup, conditional on your swing and the equipment you choose"
        : "Conventional full-swing setup with comfortable overhead margin"
    );
    workable.push(dims[3].score <= 1 ? "Narrower impact screen sized to your screen wall" : "Conventional impact screen sized to your screen wall");
    workable.push("Full enclosure with side and ceiling protection");
    workable.push(shortThrow ? "Short-throw projection so the light path clears the golfer" : "Short-throw or ceiling-mounted longer-throw projection");
  }
  if (f.handed === "Both")
    workable.push("A launch monitor that reads both left- and right-handed swings");

  const optionsHeadline =
    band === "unsupported"
      ? "Practice configurations that do work in this room"
      : band === "limited"
      ? "Configurations worth considering at this ceiling height"
      : "Configurations your room supports";

  return { status, headline, body, reasons, limitingLabel, verify: verify.slice(0, 3), screen, projector, install, workable, optionsHeadline };
}

const INITIAL = { handed: "Right-handed" };

export default function GolfFitChecker() {
  const tool = useTool(SLUG);
  const { start, complete, resetRun } = useToolEvents(SLUG);
  const [step, setStep] = useState(0);
  const [f, setF] = useState(INITIAL);
  const [showErrors, setShowErrors] = useState(false);
  const [result, setResult] = useState(null);
  const set = (k) => (v) => { start(); setF((s) => ({ ...s, [k]: v })); };
  const total = 2;

  const errs = {
    width: dimensionError(f.width, { min: 6, max: 60 }),
    length: dimensionError(f.length, { min: 6, max: 80 }),
    ceiling: dimensionError(f.ceiling, { min: 5, max: 25 }),
    screenWall: dimensionError(f.screenWall, { min: 4, max: 60 }),
  };
  const stepErrors = step === 0 ? ["width", "length", "ceiling"] : ["screenWall"];
  const stepValid = stepErrors.every((k) => !errs[k]);

  const next = () => {
    start();
    if (!stepValid) return setShowErrors(true);
    setShowErrors(false);
    if (step < total - 1) return setStep(step + 1);
    const r = evaluate(f);
    setResult(r);
    complete({ status: r.status });
  };

  if (!tool) return <div className="h-[60vh]" />;

  return (
    <ToolFrame tool={tool}>
      <Panel>
        {!result ? (
          <>
            <Progress step={step} total={total} />

            {step === 0 && (
              <Question
                label="Your room"
                hint="Measure ceiling height at its lowest point in the swing zone — with any garage door fully open, including the track, opener and light fixtures."
              >
                <div className="grid sm:grid-cols-3 gap-4">
                  <NumberField label="Room width" unit="ft" value={f.width} onChange={set("width")} testId="input-width" placeholder="e.g. 14" error={errs.width} showError={showErrors} />
                  <NumberField label="Room depth / length" unit="ft" value={f.length} onChange={set("length")} testId="input-length" placeholder="e.g. 18" error={errs.length} showError={showErrors} />
                  <NumberField label="Lowest usable ceiling height" unit="ft" value={f.ceiling} onChange={set("ceiling")} testId="input-ceiling" placeholder="e.g. 9.5" error={errs.ceiling} showError={showErrors} />
                </div>
              </Question>
            )}

            {step === 1 && (
              <>
                <Question label="Your setup" hint="Exclude outlets, panels, windows and door frames from the usable screen wall.">
                  <div className="grid sm:grid-cols-2 gap-4">
                    <NumberField label="Usable screen-wall width" unit="ft" value={f.screenWall} onChange={set("screenWall")} testId="input-screen-wall" placeholder="e.g. 12" error={errs.screenWall} showError={showErrors} />
                  </div>
                </Question>
                <Question label="Who will be using it?">
                  <Choices testId="choice-handed" options={["Right-handed", "Left-handed", "Both"]} value={f.handed} onChange={set("handed")} />
                </Question>
              </>
            )}

            <Nav step={step} total={total} onBack={() => { setShowErrors(false); setStep(Math.max(0, step - 1)); }} onNext={next} disabled={!stepValid} />
          </>
        ) : (
          <div data-testid="tool-result">
            <ResultHeadline
              eyebrow="Your room fit result"
              headline={result.headline}
              body={result.body}
              reasons={result.reasons}
              testId="result-headline"
            />
            <WorkableOptions
              tool={tool}
              headline={result.optionsHeadline}
              options={result.workable}
              productPath="Golf simulator systems, impact screens, enclosures and projection matched to your measured room"
            />
            <div className="border-t-2 border-charcoal mt-12">
              <ResultBlock label="Fit verdict" value={result.status} emphasis testId="result-status" />
              <ResultBlock label="Limiting dimension" value={result.limitingLabel} testId="result-limiting" />
              <ResultList label="What to verify next" items={result.verify} testId="result-verify" />
              <Details label="See installation details" testId="result-details">
                <ResultList label="Screen & enclosure considerations" items={result.screen} testId="result-screen" />
                <ResultList label="Projector considerations" items={result.projector} testId="result-projector" />
                <ResultList label="Installation considerations" items={result.install} testId="result-install" />
              </Details>
            </div>

            <Disclaimer>
              These ceiling ranges are general planning guidance, not manufacturer guarantees. Your
              height and swing, the enclosure, and the launch monitor's published requirements
              override generic guidance. Verify the manufacturer's installation dimensions against
              your measured room before ordering.
            </Disclaimer>

            <div className="mt-2">
              <ResultFooter onReset={() => { resetRun(); setResult(null); setStep(0); setF(INITIAL); setShowErrors(false); }} />
            </div>
          </div>
        )}
      </Panel>
    </ToolFrame>
  );
}
